/**
 * ConversationPage — one multi-turn run, assembled from its traces.
 *
 * Stats strip, per-turn metrics, flat transcript (ConversationTranscript).
 * Reached only by link: the dashboard's Conversations band and the model
 * Requests tab both render ConversationsPanel. There is no list of its
 * own — the raw request log lives on the dashboard. The back arrow returns
 * to the page the link was on (`backLink`, TRA-1412).
 */

import { ArrowLeft } from 'lucide-react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'

import { backLink } from '@/components/traces/conversationLink'
import { ConversationTranscript } from '@/components/traces/ConversationTranscript'
import { TraceKeyTranscript } from '@/components/traces/TraceKeyTranscript'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'

export default function ConversationPage(): React.JSX.Element {
  // URL-backed so the view survives reload and stays linkable. `trace_key`
  // is the alternate door (TRA-939): most production traffic writes
  // conversation-less trace keys, so the trajectories page links those runs
  // here by full R2 key and the transcript is assembled server-side from
  // that key instead.
  const [searchParams] = useSearchParams()
  const conversationId = searchParams.get('conversation_id') ?? undefined
  const traceKey = searchParams.get('trace_key') ?? undefined
  const back = backLink(useLocation().state, searchParams.get('vanity-org'))

  return (
    <PageContainer className="space-y-6">
      <Button variant="ghost" size="sm" className="-ml-2" asChild>
        <Link to={back.to}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {back.label}
        </Link>
      </Button>
      <PageHeader
        title="Conversation"
        description="See each turn of this run."
      />
      {traceKey !== undefined ? (
        <TraceKeyTranscript key={traceKey} traceKey={traceKey} />
      ) : conversationId === undefined ? (
        <p className="text-sm text-muted-foreground">
          This conversation link is missing its{' '}
          <code className="font-mono">conversation_id</code> parameter. Open it
          from the dashboard's Conversations list to see the transcript.
        </p>
      ) : (
        <ConversationTranscript
          // Remount per conversation: search/tool-filter/disclosure are
          // component state and would otherwise carry over.
          key={conversationId}
          conversationId={conversationId}
        />
      )}
    </PageContainer>
  )
}
